import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Compass, XCircle } from "lucide-react";

// One-time landing page hit from the "Complete payment" button in the approval email.
// Consumes the magic-login token, sets auth cookies, then lands on /subscribe
// so the user can review their plan card, tick the launch terms, and kick off
// Paystack themselves. After Paystack confirms the charge, PaymentCallback
// routes them to their role dashboard. We never route to the dashboard from
// this link directly — the approval email is strictly a payment entry point.
const AUTO_PAY_PLANS = {};

export default function CompletePayment() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [error, setError] = useState("");
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const token = params.get("token");
    if (!token) {
      setError("Missing payment token — please use the link from your approval email.");
      return;
    }
    (async () => {
      try {
        const { data: signedIn } = await api.post("/auth/magic-login", { token });
        setUser(signedIn);
        // Auto-payment roles (vendor, event_creator, marketplace_user): skip the
        // plan picker and drop them straight onto the Paystack payment portal
        // for their launch price.
        const autoPlan = AUTO_PAY_PLANS[signedIn?.role];
        if (autoPlan && signedIn?.subscription_status !== "active") {
          try {
            const { data: pay } = await api.post("/subscription/initialize", { plan_id: autoPlan });
            if (pay?.authorization_url) {
              window.location.href = pay.authorization_url;
              return;
            }
          } catch (_) {
            // Fall through to /subscribe if Paystack init fails so the user isn't stuck.
          }
        }
        // Invited-user payment route: users who signed up through the
        // admin invite flow (`invite_token` on their doc) and haven't yet
        // captured their card go to the standalone pricing confirmation
        // page at /invite-payment-plan instead of the standard /subscribe
        // plan picker. Everything else — including the active, already-
        // paid state — keeps the original /subscribe destination so the
        // standard flow is not affected.
        if (signedIn?.invite_token && !signedIn?.invite_trial_captured_at) {
          navigate("/invite-payment-plan", { replace: true });
          return;
        }
        // Vendors land on the new checkout page at /checkout?plan=vendor
        // (parent + other roles continue to use /subscribe unchanged).
        if (signedIn?.role === "vendor" && signedIn?.subscription_status !== "active") {
          navigate("/checkout?plan=vendor", { replace: true });
          return;
        }
        // Event creators land on the new checkout page at
        // /checkout?plan=eventcreator. All other role flows (parent,
        // marketplace user, bundle, invite-trial) stay on /subscribe.
        if (signedIn?.role === "event_creator" && signedIn?.subscription_status !== "active") {
          navigate("/checkout?plan=eventcreator", { replace: true });
          return;
        }
        // Marketplace sellers land on the new checkout page at
        // /checkout?plan=marketplace. All other role flows stay on
        // /subscribe.
        if (signedIn?.role === "marketplace_user" && signedIn?.subscription_status !== "active") {
          navigate("/checkout?plan=marketplace", { replace: true });
          return;
        }
        // Everything else — including an already-active subscription — lands on
        // the payment page, never the dashboard.
        navigate("/subscribe", { replace: true });
      } catch (e) {
        setError(errMsg(e, "This payment link could not be used. Please contact support."));
      }
    })();
  }, [params, navigate, setUser]);

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-4 grain" data-testid="complete-payment-page">
      <div className="bg-white/80 border border-sage/20 rounded-3xl p-10 max-w-md w-full text-center shadow-sm">
        {!error ? (
          <>
            <Compass className="w-12 h-12 text-sage animate-spin mx-auto mb-4" style={{ animationDuration: "2s" }} />
            <h1 className="text-xl font-black text-forest mb-2">Opening your payment page…</h1>
            <p className="text-forest/60 text-sm">One moment while we sign you in and take you to Paystack.</p>
          </>
        ) : (
          <>
            <span className="inline-flex w-16 h-16 rounded-full bg-terracotta-light items-center justify-center mb-4">
              <XCircle className="w-8 h-8 text-terracotta-dark" />
            </span>
            <h1 className="text-2xl font-black text-forest mb-2">Link couldn't be used</h1>
            <p className="text-forest/70 text-sm mb-6" data-testid="complete-payment-error">{error}</p>
            <Button className="rounded-full bg-sage hover:bg-sage-dark text-white" onClick={() => navigate("/login")} data-testid="complete-payment-back-btn">
              Back to sign in
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
